import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import {ProcedureType, StudyType} from 'graphql-client/__generated/graphqlGlobalTypes';
import getProcedureUIString, {ProceduresStringKeys, StringCase} from 'procedures/ProceduresStringKeys';
import {useExperimentProcedure} from 'studies/typed/shared/ExperimentalInputs';
import ProcedureDropdown, {ProcedureValidationLoadingSpinner} from 'studies/typed/shared/ProcedureDropdown';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {ButtonStyle} from 'ui-library/buttons/Button';
import {ChipSize} from 'ui-library/chips/Chip';
import ProcedureChip from 'ui-library/chips/ProcedureChip';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import Modal from 'ui-library/modals/Modal';
import {withLoadingScreen} from 'util/Promises';

export interface ProcedureChangeParams {
  procedureId: string;
  templateProcedureFlowchartVersionId?: string;
  workflowFlowchartConfigVersionId?: string;
}

interface EditProcedureModalProps {
  procedureId: string;
  flowchartVersionId: string;
  procedureType: ProcedureType;
  studyType: StudyType;
  onProcedureChange: (params: ProcedureChangeParams) => Promise<void>;
  onRequestClose: () => void;
}

export default function EditProcedureModal(props: EditProcedureModalProps): JSX.Element {
  const [newProcedureId, setNewProcedureId] = useState<string | null>(null);

  // Fetch the newly selected procedure's info
  const {procedure: newProcedure} = useExperimentProcedure(
    newProcedureId ? {procedureId: newProcedureId} : null,
    props.studyType
  );

  const procedureUIStringLowercase = getProcedureUIString(
    ProceduresStringKeys.PROCEDURE,
    props.procedureType,
    {stringCase: StringCase.LOWER}
  );

  // checks to see if the selected procedure info has been successfully fetched
  const isProcedureReady = newProcedure != null && newProcedureId === newProcedure.id;
  const canSubmit = newProcedureId != null && isProcedureReady;

  const handleUpdate = async (): Promise<void> => {
    if (!canSubmit) {
      return;
    }

    const workflowFlowchartConfigVersionId = newProcedure.workflowFlowchartConfigVersionId ?? undefined;
    // Procedures with workflow flowchart config versions are PoW procedures. All procedures have a procedure
    // flowchart version id
    const isPOW = workflowFlowchartConfigVersionId != null;

    await withLoadingScreen(
      props.onProcedureChange({
        procedureId: newProcedureId,
        templateProcedureFlowchartVersionId: isPOW ? undefined : newProcedure.procedureFlowchartVersionId,
        workflowFlowchartConfigVersionId: isPOW ? workflowFlowchartConfigVersionId : undefined,
      }),
      `Updating ${procedureUIStringLowercase}...`
    );
    props.onRequestClose();
  };

  return (
    <Modal
      title={`Update ${procedureUIStringLowercase}`}
      onRequestClose={props.onRequestClose}
      buttons={[
        {
          text: 'Back',
          cancel: true,
          style: ButtonStyle.TERTIARY_GRAY,
          onClick: props.onRequestClose,
        },
        {
          text: 'Update',
          submit: true,
          disabled: !canSubmit,
          onClick: handleUpdate,
        },
      ]}
    >
      <div className={css(styles.bannerContainer)}>
        <InlineBanner
          bannerType={InlineBannerType.WARNING}
          text={`Changing the ${procedureUIStringLowercase} for your design will remove values from your current design.`}
          supportingInfo="Any values you have for parameters, material inputs, or equipment will be removed."
        />
      </div>

      <div className={css(styles.fieldsContainer)}>
        <div className={css(styles.field)}>
          <FieldLabel label={`Current ${procedureUIStringLowercase}`} />
          <ProcedureChip
            id={props.procedureId}
            flowchartVersionId={props.flowchartVersionId}
            size={ChipSize.SMALL}
          />
        </div>

        <div className={css(styles.field)}>
          <FieldLabel label={`New ${procedureUIStringLowercase}`} />
          <ProcedureDropdown
            isDisabled={false}
            procedureType={props.procedureType}
            selectedProcedureId={newProcedureId}
            onChangeSelectedProcedure={setNewProcedureId}
            onRequestCloseModal={props.onRequestClose}
            procedureFetchPolicy="network-only"
            placeholderText={`Select ${procedureUIStringLowercase}`}
            excludeProcedureId={props.procedureId}
          />
          {newProcedureId != null && !isProcedureReady && (
            <ProcedureValidationLoadingSpinner procedureType={props.procedureType} />
          )}
        </div>
      </div>
    </Modal>
  );
}

const styles = StyleSheet.create({
  bannerContainer: {
    marginBottom: 20,
  },
  fieldsContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: 20,
  },
  field: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
});
